import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowUpRight, Plus, Minus } from "lucide-react";
import { institutions, site } from "../mock/mock";
import { nav } from "../mock/pages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import Logo from "./Logo";

export default function Navbar({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // solid header on inner pages, or once scrolled on the home page
  const solid = !transparent || scrolled;
  const linkColor = solid ? "var(--teal-800)" : "rgba(255,255,255,.92)";

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to.split("#")[0]));

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50">
        {/* Top utility strip */}
        <div
          className="hidden lg:block"
          style={{ maxHeight: scrolled ? 0 : 44, overflow: "hidden", transition: "max-height .4s ease" }}
        >
          <div className="text-[0.76rem]" style={{ background: "var(--teal-950)", color: "rgba(251,247,240,.7)" }}>
            <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-2.5">
              <span className="tracking-wide">CBSE · Pre-KG to Grade VII · {site.location}</span>
              <div className="flex items-center gap-5">
                <span style={{ color: "var(--gold-200)" }} className="font-semibold">
                  Admission Hotline
                </span>
                {site.hotlines.map((h) => (
                  <a
                    key={h}
                    href={`tel:${h.replace(/\s/g, "")}`}
                    className="flex items-center gap-2 hover:text-white"
                    style={{ transition: "color .25s" }}
                  >
                    <Phone size={13} /> {h}
                  </a>
                ))}
                <span className="opacity-40">|</span>
                <a href={`mailto:${site.email}`} className="hover:text-white" style={{ transition: "color .25s" }}>
                  {site.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        <header
          className="relative"
          style={{
            background: solid ? "rgba(251,247,240,.94)" : "transparent",
            backdropFilter: solid ? "blur(14px)" : "none",
            borderBottom: solid ? "1px solid rgba(11,59,54,.1)" : "1px solid transparent",
            transition: "background .4s ease, border-color .4s ease, box-shadow .4s ease",
            boxShadow: solid ? "0 10px 30px -24px rgba(8,46,41,.5)" : "none",
          }}
        >
          <nav className="mx-auto flex max-w-[1280px] items-center justify-between px-5 py-3.5 lg:px-6">
            <Link to="/" className="flex items-center">
              <Logo variant={solid ? "light" : "dark"} height={scrolled ? 42 : 48} />
            </Link>

            {/* Desktop nav */}
            <div className="hidden items-center gap-6 xl:gap-7 lg:flex">
              {nav.map((item) =>
                item.children ? (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1.5 text-[0.87rem] font-medium outline-none"
                        style={{
                          color: isActive(item.to) && solid ? "var(--gold)" : linkColor,
                          transition: "color .3s ease",
                        }}
                      >
                        {item.label} <ChevronDown size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[260px] p-2">
                      {item.children.map((c) => (
                        <DropdownMenuItem key={c.to} asChild className="cursor-pointer rounded-lg px-3 py-2.5">
                          <Link to={c.to} className="block text-[0.86rem] font-medium" style={{ color: "var(--teal-800)" }}>
                            {c.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="underline-grow text-[0.87rem] font-medium"
                    style={{
                      color: isActive(item.to) && solid ? "var(--gold)" : linkColor,
                      transition: "color .3s ease",
                    }}
                  >
                    {item.label}
                  </Link>
                )
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1.5 text-[0.87rem] font-medium outline-none"
                    style={{ color: linkColor, transition: "color .3s ease" }}
                  >
                    Institutions <ChevronDown size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[330px] p-2">
                  {institutions.map((i) => (
                    <DropdownMenuItem key={i.short} asChild className="cursor-pointer rounded-lg p-3">
                      <a href={i.url} target="_blank" rel="noreferrer" className="block">
                        <span
                          className="flex items-center justify-between text-[0.9rem] font-semibold"
                          style={{ color: "var(--teal-800)" }}
                        >
                          {i.short} <ArrowUpRight size={14} />
                        </span>
                        <span className="mt-0.5 block text-[0.76rem]" style={{ color: "var(--muted-ink)" }}>
                          {i.name}
                        </span>
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem asChild className="cursor-pointer rounded-lg p-3">
                    <Link to="/about#ecosystem" className="text-[0.82rem] font-medium" style={{ color: "var(--gold)" }}>
                      The KPR learning ecosystem
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <a
                href={site.applyUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full px-6 py-2.5 text-[0.85rem] font-semibold"
                style={{
                  background: "var(--gold)",
                  color: "#fff",
                  transition: "transform .3s ease, box-shadow .3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 26px -14px rgba(192,138,46,.85)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Apply Now
              </a>
            </div>

            <button
              className="rounded-lg p-2 lg:hidden"
              aria-label="Menu"
              onClick={() => setOpen(true)}
              style={{ color: solid ? "var(--teal-800)" : "#fff" }}
            >
              <Menu size={24} />
            </button>
          </nav>
        </header>
      </div>

      {/* Mobile drawer */}
      <div
        className="fixed inset-0 z-[60] lg:hidden"
        style={{
          pointerEvents: open ? "auto" : "none",
          opacity: open ? 1 : 0,
          transition: "opacity .3s ease",
        }}
      >
        <div className="absolute inset-0" style={{ background: "rgba(5,32,29,.5)" }} onClick={() => setOpen(false)} />
        <div
          className="absolute right-0 top-0 h-full w-[88%] max-w-[390px] overflow-y-auto p-6"
          style={{
            background: "var(--cream)",
            transform: open ? "translateX(0)" : "translateX(100%)",
            transition: "transform .42s cubic-bezier(.22,1,.36,1)",
          }}
        >
          <div className="flex items-center justify-between">
            <Logo variant="light" height={34} />
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ color: "var(--teal-800)" }}>
              <X size={22} />
            </button>
          </div>
          <div className="hairline-gold my-5" />

          <div className="flex flex-col">
            {nav.map((item) =>
              item.children ? (
                <div key={item.label} style={{ borderBottom: "1px solid rgba(11,59,54,.08)" }}>
                  <button
                    onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                    className="font-display flex w-full items-center justify-between py-3.5 text-[1.2rem]"
                    style={{ color: "var(--teal-800)" }}
                  >
                    {item.label}
                    {expanded === item.label ? <Minus size={16} /> : <Plus size={16} />}
                  </button>
                  {expanded === item.label && (
                    <div className="pb-3 pl-1">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          className="block py-2 text-[0.9rem]"
                          style={{ color: "var(--muted-ink)" }}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="font-display py-3.5 text-[1.2rem]"
                  style={{ color: "var(--teal-800)", borderBottom: "1px solid rgba(11,59,54,.08)" }}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="mt-6">
            <p className="eyebrow" style={{ color: "var(--gold)", fontSize: "0.66rem" }}>
              KPR Institutions
            </p>
            {institutions.map((i) => (
              <a
                key={i.short}
                href={i.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 flex items-center justify-between text-[0.84rem]"
                style={{ color: "var(--muted-ink)" }}
              >
                {i.name} <ArrowUpRight size={14} />
              </a>
            ))}
          </div>

          <div className="mt-7 rounded-2xl p-5" style={{ background: "var(--sand)" }}>
            <p className="eyebrow" style={{ color: "var(--gold)", fontSize: "0.66rem" }}>
              Admission hotline
            </p>
            {site.hotlines.map((h) => (
              <a
                key={h}
                href={`tel:${h.replace(/\s/g, "")}`}
                className="font-display mt-2 block text-[1.05rem]"
                style={{ color: "var(--teal-900)" }}
              >
                {h}
              </a>
            ))}
          </div>

          <a
            href={site.applyUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className="mt-6 block w-full rounded-full py-3.5 text-center text-[0.9rem] font-semibold"
            style={{ background: "var(--teal-800)", color: "#fff" }}
          >
            Apply Now
          </a>
        </div>
      </div>
    </>
  );
}
